/**
 * Multi Internet card engine — a TypeScript port of the design's `bootMultiInternet` (app.js §3).
 *
 * React renders the card's markup once; this engine hydrates it in place: it owns the SVG wires, the packet
 * layer (both rendered empty by React), every class / text / aria state the simulation changes, the auto-demo
 * script, the "Simular falha" and pause controls, the sparklines and the WAAPI packets. `destroy()` undoes all
 * of it (timers, frames, observers, listeners, animations, built nodes), so it survives StrictMode and a
 * language switch (the component simply restarts it with the new strings).
 */
/* eslint-disable no-param-reassign -- the engine's state lives in plain mutable records (connections, packets, segments), as in the design */
/* eslint-disable @typescript-eslint/no-use-before-define -- mutually recursive step functions (syncDerived ⇄ layout ⇄ motion), hoisted like the design's */
import type { GeoData, RawGeo } from '../geo/geo';
import { GEO_ENDPOINT, sharedGeo } from '../geo/geo';
import { reducedMotionQuery } from '../hooks/use-reduced-motion';
import { MI_TOTAL } from './connections';

export type MiTranslate = (key: string, options?: Record<string, unknown>) => string;

export interface MiEngineOptions {
  /** `t` bound to the v1a namespace; keys are read under `mi.` */
  t: MiTranslate;
  /** BCP 47 tag for Intl (numbers, lists) */
  lang: string;
  data: GeoData;
}

export interface MiEngine {
  destroy: () => void;
}

interface Rect {
  l: number;
  t: number;
  r: number;
  b: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

interface Packet {
  el: HTMLDivElement;
  phase: number;
  anim: Animation | null;
}

interface Segment {
  d: string;
  parks: number[];
  packets: Packet[];
  frames: Keyframe[] | null;
  parkIdx: number[] | null;
  len?: number;
  hide?: Array<{ l: number; t: number; r: number; b: number }> | null;
  offT?: number;
}

interface Trunk extends Segment {
  g: SVGGElement | null;
  set: HTMLDivElement | null;
  base: SVGPathElement | null;
  glow: SVGPathElement | null;
  hot: SVGPathElement | null;
  wasOn: boolean | null;
  offT: number;
  len: number;
  live: number;
}

interface Connection {
  id: string;
  mbps: number;
  brand: string;
  kind: string;
  name: string;
  sr: string;
  on: boolean;
  down: boolean;
  wasOn: boolean;
  wasDown?: boolean;
  offT: number;
  hist: number[];
  el: HTMLElement;
  ccEl: HTMLElement;
  tagEl: HTMLElement;
  mbEl: HTMLElement;
  deltaEl: HTMLElement;
  spLine: SVGPolylineElement;
  spFill: SVGPolygonElement;
  spDot: SVGCircleElement;
  g?: SVGGElement;
  pg?: SVGGElement;
  baseIn?: SVGPathElement;
  baseOut?: SVGPathElement;
  glowIn?: SVGPathElement;
  glowOut?: SVGPathElement;
  hotIn?: SVGPathElement;
  hotOut?: SVGPathElement;
  cut?: SVGGElement;
  portIn?: SVGCircleElement;
  portOut?: SVGCircleElement;
  set?: HTMLDivElement;
  segIn?: Segment;
  segOut?: Segment;
  stubD?: string;
  cutXY?: string;
  busR?: { x: number; tile: Rect; card: Rect } | null;
}

interface Capacity {
  n: string;
  u: string;
  sr: string;
}

type ProviderPick = { brand: string; kind: string; mine?: boolean };
type Provider = [string, string | boolean];

const NS = 'http://www.w3.org/2000/svg';

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ?? character);
}

/** The JSON templates use a tiny markup vocabulary; this maps it to the card's classes. */
function rich(template: string): string {
  return template
    .replace(/<bbad>/g, '<b class="mi-bad">')
    .replace(/<\/bbad>/g, '</b>')
    .replace(/<bad>/g, '<span class="mi-bad">')
    .replace(/<\/bad>/g, '</span>')
    .replace(/<stab>/g, '<b class="mi-stab">')
    .replace(/<\/stab>/g, '</b>');
}

function fold(text: unknown): string {
  return String(text || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9&]+/g, ' ')
    .trim();
}

function normGeo(raw: RawGeo): { country: string; isp: string } | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const countryField = record.country;
  const code = record.country_code || record.countryCode || (countryField && typeof countryField === 'object' ? (countryField as Record<string, unknown>).code : countryField);
  if (!code || String(code).length !== 2) return null;
  const connection = record.connection as Record<string, unknown> | undefined;
  const isp = record.isp || record.org || record.asn_org || (connection && (connection.isp || connection.org)) || '';
  return { country: String(code).toUpperCase(), isp: String(isp) };
}

function forcedGeo(): { country: string; isp: string } | null {
  try {
    const query = window.location.search;
    const geoMatch = /[?&]mcgeo=([^&#]*)/.exec(query);
    const ispMatch = /[?&]mcisp=([^&#]*)/.exec(query);
    if (!geoMatch) return null;
    return normGeo({ country: decodeURIComponent((geoMatch[1] ?? '').replace(/\+/g, ' ')).split('|')[0], isp: ispMatch ? decodeURIComponent((ispMatch[1] ?? '').replace(/\+/g, ' ')) : '' });
  } catch {
    return null;
  }
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

export function startMultiInternet(root: HTMLElement, { t, lang, data }: MiEngineOptions): MiEngine {
  const disposers: Array<() => void> = [];
  let destroyed = false;
  const tr = (key: string, vars?: Record<string, unknown>) => t(`mi.${key}`, { ...vars, interpolation: { escapeValue: false } });

  const query = <T extends Element>(selector: string, scope: ParentNode = root): T => {
    const found = scope.querySelector<T>(selector);
    if (!found) throw new Error(`multi-internet: missing ${selector}`);
    return found;
  };

  root.classList.add('is-js');
  const stage = query<HTMLElement>('[data-stage]');
  const svg = query<SVGSVGElement>('[data-wires]');
  const pkLayer = query<HTMLElement>('[data-packets]');
  const list = query<HTMLElement>('[data-routes]');
  const pcNode = query<HTMLElement>('[data-node="pc"]');
  const npNode = query<HTMLElement>('[data-node="np"]');
  const gameNode = query<HTMLElement>('[data-node="game"]');
  const pcIcon = query<HTMLElement>('.mi-node-icon', pcNode);
  const npIcon = query<HTMLElement>('.mi-node-icon', npNode);
  const gameIcon = query<HTMLElement>('.mi-node-icon', gameNode);
  const npSub = query<HTMLElement>('[data-np-sub]');
  const gameSub = query<HTMLElement>('[data-game-sub]');
  const statusText = query<HTMLElement>('[data-status-text]');
  const liveEl = query<HTMLElement>('[data-live]');
  const bridge = query<HTMLElement>('[data-bridge]');
  const dropBtn = query<HTMLButtonElement>('[data-drop]');
  const dropLbl = query<HTMLElement>('[data-lbl]', dropBtn);
  const pauseBtn = query<HTMLButtonElement>('[data-pause]');
  const hintText = query<HTMLElement>('[data-hint]');
  const keyEl = query<HTMLElement>('[data-key]');
  const keyLabel = query<HTMLElement>('[data-key-label]');
  const backupSub = query<HTMLElement>('[data-backup-sub]');
  const backupB = query<HTMLElement>('[data-backup-b]');
  const pBase = query<HTMLElement>('[data-p-base]');
  const pBaseU = query<HTMLElement>('[data-p-base-u]');
  const pSum = query<HTMLElement>('[data-p-sum]');
  const pSumU = query<HTMLElement>('[data-p-sum-u]');
  const pPct = query<HTMLElement>('[data-p-pct]');
  const pFill = query<HTMLElement>('[data-p-fill]');
  const pFillGood = query<HTMLElement>('[data-p-fill-good]');
  const pDrops = query<HTMLElement>('[data-p-drops]');
  const proofSr = root.querySelector<HTMLElement>('[data-proof-sr]');
  const sumEl = query<HTMLElement>('[data-sum]');
  const sumU = query<HTMLElement>('[data-sum-u]');
  const sumStrong = sumEl.parentElement as HTMLElement;
  const sumB = query<HTMLElement>('[data-sum-b]');
  const sumSub = query<HTMLElement>('[data-sum-sub]');
  const sumRow = query<HTMLElement>('[data-sum-row]');
  const srNow = root.querySelector<HTMLElement>('[data-sr-now]');
  const miDesc = root.querySelector<HTMLElement>('[data-mi-desc]');

  let resultNote: string | null = null;
  // the match's drop counter: it goes to 1 only when a lone connection fails (no reserve), and it stays there until a
  // second connection is active, so the row never contradicts its own caption
  let kicked = false;

  const backupPair = (state: 'rest' | 'hot' | 'lone' | 'none'): [string, string] => [tr(`backup.${state}_b`), tr(`backup.${state}_sub`)];

  const numberFormat = (() => {
    try {
      return new Intl.NumberFormat(lang, { maximumFractionDigits: 2, numberingSystem: 'latn' } as Intl.NumberFormatOptions);
    } catch {
      return new Intl.NumberFormat('en', { maximumFractionDigits: 2 });
    }
  })();
  const formatNumber = (value: number) => numberFormat.format(value);
  const joinList = (items: string[]): string => {
    try {
      return new Intl.ListFormat(lang, { type: 'conjunction' }).format(items);
    } catch {
      return items.join(', ');
    }
  };

  // product terms never break across lines ("Wi-Fi 6", "5G do celular")
  const nb = (text: string) => `<span class="nb">${escapeHtml(text)}</span>`;
  // long / short wording, picked by the same container branch as the key label
  const alt = (long: string, short: string) => (long === short ? long : `<span class="x-long">${long}</span><span class="x-short">${short}</span>`);
  const altKey = (key: string, vars: Record<string, unknown>) => alt(tr(`${key}_long`, vars), tr(`${key}_short`, vars));

  function fmtCap(mbps: number): Capacity {
    if (!mbps) return { n: '-', u: '', sr: tr('cap_none') };
    if (mbps < 1000) return { n: formatNumber(mbps), u: tr('unit_mb'), sr: tr('cap_mb', { value: formatNumber(mbps) }) };
    const text = formatNumber(Math.round(mbps / 10) / 100);
    return { n: text, u: tr('unit_gb'), sr: tr('cap_gb', { value: text }) };
  }

  const reduced = reducedMotionQuery();
  const isMobile = () => getComputedStyle(stage).getPropertyValue('--mi-mobile').trim() === '1';

  /* ---------- data ---------- */
  const KIND: Record<string, string> = { fiber: tr('kind.fiber'), cable: tr('kind.cable'), dsl: tr('kind.dsl'), wireless: tr('kind.wireless'), satellite: tr('kind.satellite') };
  const PROVIDERS = data.providers;

  const CONNS: Connection[] = Array.from(list.querySelectorAll<HTMLElement>('[data-conn]')).map((element) => {
    const id = element.getAttribute('data-conn') ?? '';
    const name = tr(`conns.${id}.name`);
    return {
      id,
      mbps: Number(element.getAttribute('data-mbps')) || 0,
      brand: name,
      kind: tr(`conns.${id}.kind`),
      name,
      sr: tr(`conns.${id}.sr`),
      on: element.classList.contains('is-on'),
      down: false,
      wasOn: element.classList.contains('is-on'),
      offT: 0,
      hist: [],
      el: element,
      ccEl: query<HTMLElement>('.mi-route-cc', element),
      tagEl: query<HTMLElement>('[data-tag]', element),
      mbEl: query<HTMLElement>('[data-mb]', element),
      deltaEl: query<HTMLElement>('[data-delta]', element),
      spLine: query<SVGPolylineElement>('.mi-route-spark polyline', element),
      spFill: query<SVGPolygonElement>('.mi-route-spark .sp-fill', element),
      spDot: query<SVGCircleElement>('.mi-route-spark .sp-dot', element),
    };
  });
  const TOTAL = CONNS.length || MI_TOTAL;

  /* slot → { brand, kind, mine } for a country: the visitor's ISP first (when known), then fibre, cable and the other fixed
     providers, then the mobile operators; a brand is used once while the lists allow */
  function pickProviders(countryCode: string, isp: string): Record<string, ProviderPick> {
    const pool = PROVIDERS[countryCode] || PROVIDERS.US || { fixed: [], mobile: [] };
    const used: Record<string, number> = {};
    const out: Record<string, ProviderPick> = {};
    const fixed = (pool.fixed || []) as Provider[];
    const mobile = (pool.mobile || []) as Provider[];
    const take = (candidates: Provider[], accept?: (provider: Provider) => boolean): Provider | null => {
      const found = candidates.find((provider) => !used[fold(provider[0])] && (!accept || accept(provider)));
      if (found) used[fold(found[0])] = 1;
      return found ?? null;
    };
    const any = (candidates: Provider[], index: number): Provider | null => (candidates.length ? (candidates[index % candidates.length] ?? null) : null);
    let mine: Provider | null = null;
    const ispKey = fold(isp);
    if (ispKey) {
      fixed.concat(mobile).forEach((provider) => {
        const providerKey = fold(provider[0]);
        if (!mine && providerKey && (ispKey.indexOf(providerKey) >= 0 || providerKey.indexOf(ispKey) >= 0)) mine = provider;
      });
      if (mine) used[fold((mine as Provider)[0])] = 1;
    }
    const isMobileProvider = (provider: Provider) => mobile.indexOf(provider) >= 0;
    const mobileKind = (provider: Provider) => (provider[1] === true ? '5G' : '4G');
    const kindOf = (provider: Provider) => (isMobileProvider(provider) ? mobileKind(provider) : KIND[String(provider[1])] || KIND.fiber || '');
    const fallback: Provider = ['NoPing', 'fiber'];
    const fib = mine || take(fixed, (provider) => provider[1] === 'fiber') || take(fixed) || any(fixed, 0) || any(mobile, 0) || fallback;
    out.fibra = { brand: fib[0], kind: kindOf(fib), mine: !!mine };
    const cab = take(fixed, (provider) => provider[1] === 'cable') || take(fixed) || any(fixed, 1) || fib;
    out.cabo = { brand: cab[0], kind: kindOf(cab) };
    const two = take(fixed) || any(fixed, 2) || cab;
    out.isp2 = { brand: two[0], kind: kindOf(two) };
    const wifi = take(fixed) || any(fixed, 3) || fib;
    out.wifi = { brand: wifi[0], kind: isMobileProvider(wifi) ? kindOf(wifi) : 'Wi-Fi 6' };
    // mobile rows: a brand already on a fixed row is fine ("Vivo Fibra" + "Vivo 4G"), the same operator twice is not
    const m5 = take(mobile, (provider) => provider[1] === true) || take(mobile) || any(mobile, 0) || fib;
    out.g5 = { brand: m5[0], kind: isMobileProvider(m5) && m5[1] === true ? '5G' : '4G' };
    const m4 = take(mobile) || mobile.filter((provider) => provider !== m5)[0] || any(mobile, 1) || m5;
    out.g4 = { brand: m4[0], kind: '4G' };
    return out;
  }

  function applyProviders(selection: Record<string, ProviderPick>) {
    const count: Record<string, number> = {};
    CONNS.forEach((connection) => {
      const pick = selection[connection.id];
      if (pick) count[pick.brand] = (count[pick.brand] || 0) + 1;
    });
    CONNS.forEach((connection) => {
      const pick = selection[connection.id];
      if (!pick) return;
      connection.brand = pick.brand;
      connection.kind = pick.mine ? tr('kind.mine', { kind: pick.kind }) : pick.kind;
      connection.name = (count[pick.brand] ?? 0) > 1 ? `${pick.brand} ${pick.kind}` : pick.brand;
      const spoken = `${pick.brand} ${pick.kind}`;
      connection.sr = pick.mine ? tr('kind.mine_sr', { name: spoken }) : spoken;
    });
  }

  const forced = forcedGeo();
  applyProviders(pickProviders(forced?.country || 'US', forced?.isp || ''));

  /* ---------- throughput sparkline ---------- */
  const SP_W = 52;
  const SP_H = 18;
  const HIST_N = 8;
  const spLevel = (connection: Connection) => {
    if (connection.down) return 0;
    return connection.on ? 0.78 : 0.14;
  };
  const spSample = (connection: Connection) => {
    const level = spLevel(connection);
    if (!level) return 0;
    const amp = connection.on ? 0.16 : 0.05;
    return Math.max(0.04, Math.min(0.96, level + (Math.random() * 2 - 1) * amp));
  };
  const seedHist = (connection: Connection) => {
    connection.hist = Array.from({ length: HIST_N }, () => spSample(connection));
  };
  function drawSpark(connection: Connection) {
    const count = connection.hist.length;
    const points = connection.hist.map((level, index) => `${round1(1.5 + ((SP_W - 3) * index) / (HIST_N - 1))},${round1(SP_H - 1.5 - (SP_H - 3) * level)}`);
    connection.spLine.setAttribute('points', points.join(' '));
    connection.spFill.setAttribute('points', `1.5,${SP_H} ${points.join(' ')} ${round1(1.5 + ((SP_W - 3) * (count - 1)) / (HIST_N - 1))},${SP_H}`);
    const last = (points[count - 1] ?? '0,0').split(',');
    connection.spDot.setAttribute('cx', last[0] ?? '0');
    connection.spDot.setAttribute('cy', last[1] ?? '0');
  }
  function pushSample(connection: Connection) {
    connection.hist.push(spSample(connection));
    if (connection.hist.length > HIST_N) connection.hist.shift();
    drawSpark(connection);
  }
  let sparkTimer = 0;
  const sparkTick = () => CONNS.forEach(pushSample);
  function sparksRun(on: boolean) {
    window.clearInterval(sparkTimer);
    sparkTimer = 0;
    if (on) sparkTimer = window.setInterval(sparkTick, 640);
  }
  CONNS.forEach((connection) => {
    seedHist(connection);
    drawSpark(connection);
  });

  const active = () => CONNS.filter((connection) => connection.on && !connection.down);
  const byId = (id: string | null) => CONNS.find((connection) => connection.id === id) ?? null;
  const combo = (count: number) => tr('combo', { count });

  function labelFor(connection: Connection): string {
    const vars = { name: connection.sr, mbps: connection.mbps };
    if (connection.down) return tr(active().length ? 'conn_aria_down_covered' : 'conn_aria_down_lone', vars);
    return tr(connection.on ? 'conn_aria_on' : 'conn_aria_off', vars);
  }

  function paintConns() {
    CONNS.forEach((connection) => {
      const title = connection.el.querySelector('.mi-route-city');
      const kind = connection.el.querySelector('.mi-route-kind');
      if (title) title.textContent = kind ? connection.brand : `${connection.brand} ${connection.kind}`;
      if (kind) kind.textContent = connection.kind;
      connection.el.setAttribute('aria-label', labelFor(connection));
    });
    if (miDesc) miDesc.textContent = tr('desc', { list: joinList(CONNS.map((connection) => connection.sr)) });
  }
  paintConns();
  if (!forced && GEO_ENDPOINT && typeof fetch === 'function') {
    sharedGeo().then((raw) => {
      if (destroyed) return;
      const geo = normGeo(raw);
      if (geo) {
        applyProviders(pickProviders(geo.country, geo.isp));
        paintConns();
        syncDerived();
      }
    });
  }

  /* ---------- state ---------- */
  let paused = false;
  let dropping = false;
  let visible = true;
  let running = false;
  let built = false;
  let hoverId: string | null = null;
  let stepTimer = 0;
  let dropTimer = 0;
  const supportsMotion = typeof pkLayer.animate === 'function';
  const BASE_MS = 1600;
  const boost = 1;
  const durFor = (connection: Connection) => 1500 - connection.mbps * 0.9;
  const rateFor = (connection: Connection) => (BASE_MS / durFor(connection)) * boost;

  /* one-shot CSS effects restart through a class dropped now and put back on the next frame (one rAF per burst) */
  let pulseQueue: Array<{ el: Element; cls: string }> = [];
  let pulseRaf = 0;
  function pulseFlush() {
    pulseRaf = 0;
    const queued = pulseQueue;
    pulseQueue = [];
    queued.forEach((item) => item.el.classList.add(item.cls));
  }
  function pulse(element: Element | null | undefined, cls: string) {
    if (reduced.matches || !element) return;
    if (!element.classList.contains(cls)) {
      element.classList.add(cls);
      return;
    }
    element.classList.remove(cls);
    if (pulseQueue.some((item) => item.el === element && item.cls === cls)) return;
    pulseQueue.push({ el: element, cls });
    if (!pulseRaf) pulseRaf = requestAnimationFrame(pulseFlush);
  }
  const flip = (element: Element) => pulse(element, 'is-flip');
  function swapText(element: HTMLElement, text: string, hot?: boolean) {
    if (element.textContent === text && (hot === undefined || element.classList.contains('is-failover') === !!hot)) return;
    element.textContent = text;
    if (hot !== undefined) element.classList.toggle('is-failover', !!hot);
    pulse(element, 'is-swap');
  }

  const TRUNK_PHASES_DESK = [0.12, 0.55];
  const TRUNK_PHASES_BUS = [0.03, 0.3, 0.5, 0.82];
  const trunk: Trunk = { g: null, set: null, base: null, glow: null, hot: null, packets: [], frames: null, parkIdx: null, d: '', parks: [0.22, 0.7], wasOn: null, offT: 0, len: 0, hide: null, live: 2 };
  const fan: { g: SVGGElement | null; pg: SVGGElement | null } = { g: null, pg: null };
  const ports: Record<string, SVGCircleElement> = {};
  let busMode = false;
  let busGeo: { x: number; pcB: number; npT: number; npB: number; gmT: number } | null = null;
  let busKey = '';
  let wasKey = '';
  let hintGone = false;

  function stabHtml(): string {
    const count = active().length;
    if (!count) return `<b class="mi-stab is-off">${escapeHtml(tr('stab.none'))}</b>`;
    if (count === 1) return `<b class="mi-stab is-off">${escapeHtml(tr('stab.lone'))}</b>`;
    return `<b class="mi-stab">${escapeHtml(tr('stab.ok'))}</b>`;
  }
  /* raw = the line carries its own green phrase */
  function say(html: string, fade: boolean, raw?: boolean) {
    const full = raw ? html : tr('say.join', { stab: stabHtml(), text: html });
    if (statusText.innerHTML === full) return;
    statusText.innerHTML = full;
    if (fade) pulse(statusText, 'is-swap');
  }
  const sayKey = (key: string, vars: Record<string, unknown>, fade: boolean, raw?: boolean) => say(rich(tr(key, vars)), fade, raw);
  function live(message: string) {
    liveEl.textContent = '';
    liveEl.textContent = message;
  }
  function setBackup(pair: [string, string]) {
    if (backupSub.textContent === pair[1] && backupB.textContent === pair[0]) return;
    const [headline, descriptor] = pair;
    backupB.textContent = headline;
    backupSub.textContent = descriptor;
    pulse(backupSub, 'is-swap');
  }
  function burstNode() {
    if (reduced.matches) return;
    pulse(npIcon, 'is-burst');
    pulse(sumStrong, 'is-hit');
  }
  let snapRaf = 0;
  let snapTimer = 0;
  let snapEls: Element[] = [];
  function unsnap() {
    snapRaf = 0;
    window.clearTimeout(snapTimer);
    snapTimer = 0;
    const snapped = snapEls;
    snapEls = [];
    snapped.forEach((element) => element.classList.remove('is-snap'));
  }
  function snap(elements: Array<Element | undefined>) {
    elements.forEach((element) => {
      if (!element) return;
      element.classList.add('is-snap');
      snapEls.push(element);
    });
    if (snapRaf) return;
    snapRaf = requestAnimationFrame(() => {
      snapRaf = requestAnimationFrame(unsnap);
    });
    snapTimer = window.setTimeout(unsnap, 200);
  }

  const setPackets = (segment: Connection | Trunk): Packet[] => {
    if ('packets' in segment && Array.isArray(segment.packets)) return segment.packets;
    const connection = segment as Connection;
    return (connection.segIn?.packets ?? []).concat(connection.segOut?.packets ?? []);
  };
  function setPlay(owner: Connection | Trunk, on: boolean) {
    const packets = setPackets(owner);
    window.clearTimeout(owner.offT);
    owner.offT = 0;
    if (on) {
      if (running) packets.forEach((packet) => packet.anim && packet.anim.playState !== 'running' && packet.anim.play());
      return;
    }
    owner.offT = window.setTimeout(() => {
      owner.offT = 0;
      packets.forEach((packet) => packet.anim?.pause());
    }, 460);
  }

  /* every figure on screen derives from CONNS in this one pass */
  function syncDerived() {
    const act = active();
    const any = act.length > 0;
    const anyDown = CONNS.some((connection) => connection.down);
    const sum = act.reduce((total, connection) => total + connection.mbps, 0);
    const fAll = fmtCap(sum);
    const keyNow = `${act.length}|${any ? 1 : 0}`;
    keyLabel.textContent = tr('key', { count: act.length, total: TOTAL });
    if (wasKey && keyNow !== wasKey) flip(keyEl);
    wasKey = keyNow;
    CONNS.forEach((connection) => {
      const on = connection.on && !connection.down;
      const { down } = connection;
      const changed = on !== connection.wasOn || down !== connection.wasDown;
      if (down !== connection.wasDown && connection.wasDown !== undefined) snap([connection.el, connection.g, connection.pg]);
      connection.el.classList.toggle('is-on', on);
      connection.el.classList.toggle('is-down', down);
      connection.el.classList.toggle('is-off', !on && !down);
      connection.el.setAttribute('aria-pressed', on ? 'true' : 'false');
      connection.el.setAttribute('aria-label', labelFor(connection));
      const stateKey = on ? 'on' : 'off';
      connection.tagEl.textContent = tr(down ? 'tag_down' : `tag_${stateKey}`);
      connection.mbEl.textContent = down ? '0' : formatNumber(connection.mbps);
      connection.deltaEl.textContent = tr(down ? 'delta_down' : `delta_${stateKey}`);
      if (connection.g && connection.pg) {
        connection.g.classList.toggle('is-on', on);
        connection.g.classList.toggle('is-down', down);
        connection.g.classList.toggle('is-off', !on && !down);
        connection.pg.classList.toggle('is-on', on);
        connection.pg.classList.toggle('is-down', down);
        if (down !== connection.wasDown) syncStub(connection);
      }
      if (connection.set) connection.set.classList.toggle('is-on', on && !busMode);
      if (changed) {
        // the sparkline reacts at once: a drop plunges to zero, a (re)entry ramps up; static modes redraw the whole trace
        if (sparkTimer) pushSample(connection);
        else {
          seedHist(connection);
          drawSpark(connection);
        }
        if (connection.segIn) setPlay(connection, on);
      }
      connection.wasOn = on;
      connection.wasDown = down;
    });
    if (busMode) updateBusTrunk();
    if (trunk.g) {
      trunk.g.classList.toggle('is-on', any);
      trunk.g.classList.toggle('is-off', !any);
    }
    if (trunk.set) {
      trunk.set.classList.toggle('is-on', any);
      if (any !== trunk.wasOn) {
        setPlay(trunk, any);
        trunk.wasOn = any;
      }
    }
    if (ports.npIn) ['pcOut', 'npIn', 'npOut', 'gameIn'].forEach((portKey) => ports[portKey]?.classList.toggle('is-hot', any));
    root.classList.toggle('is-offline', !any);
    // ONE connection carrying the game IS the no-reserve state, except while the failover payoff is being told
    const bad = anyDown && !any;
    const noRes = act.length === 1;
    const telling = anyDown && any && !!resultNote;
    const lone = noRes && !telling;
    if (act.length >= 2) kicked = false;
    const counted = bad || (kicked && noRes);
    root.classList.toggle('is-weak', lone);
    // the proof card: "uma internet só" is the fastest active link, "Com Multi Internet" all of them combined
    const fastest = act.reduce((best, connection) => Math.max(best, connection.mbps), 0);
    const fBase = fmtCap(fastest);
    const pctN = fastest ? Math.round(((sum - fastest) / fastest) * 100) : 0;
    pBase.textContent = fastest ? fBase.n : '0';
    pBaseU.textContent = fastest ? fBase.u : tr('unit_mb');
    pSum.textContent = sum ? fAll.n : '0';
    pSumU.textContent = sum ? fAll.u : tr('unit_mb');
    pPct.textContent = tr('pct', { value: formatNumber(pctN) });
    pPct.classList.toggle('is-nil', !(pctN > 0));
    const wBad = sum ? Math.round((fastest / sum) * 100) : 0;
    const wGood = sum ? 100 : 0;
    pFill.style.setProperty('--mi-fill', `${wBad}%`);
    pFill.classList.toggle('is-empty', !wBad);
    pFillGood.style.setProperty('--mi-fill', `${wGood}%`);
    pFillGood.classList.toggle('is-empty', !wGood);
    const dropsText = tr('drops', { count: counted ? 1 : 0 });
    pDrops.textContent = dropsText;
    pDrops.classList.toggle('is-bad', counted);
    if (proofSr) proofSr.textContent = tr(pctN > 0 ? 'proof_sr_gain' : 'proof_sr', { base: fastest ? fBase.sr : tr('cap_none'), sum: sum ? fAll.sr : tr('cap_none'), pct: formatNumber(pctN), drops: dropsText });
    // the backup card and the trunk glow are held for the whole outage and released on recovery
    const failover = anyDown && any && !lone;
    stage.classList.toggle('is-failover', failover);
    let backupState: 'rest' | 'hot' | 'lone' | 'none' = 'rest';
    if (!any) backupState = 'none';
    else if (failover) backupState = 'hot';
    else if (noRes) backupState = 'lone';
    setBackup(backupPair(backupState));
    if (srNow) {
      let tail = tr('sr_now.tail_ok');
      if (lone) tail = tr('sr_now.tail_lone');
      else if (anyDown) tail = tr('sr_now.tail_failover');
      if (bad) srNow.textContent = tr('sr_now.out');
      else if (!any) srNow.textContent = tr('sr_now.none');
      else srNow.textContent = tr(act.length >= 2 ? 'sr_now.many' : 'sr_now.one', { combo: combo(act.length), total: TOTAL, cap: fAll.sr, drops: dropsText, tail });
    }
    let npState = 'np_sub.all';
    if (!any) npState = anyDown ? 'np_sub.no_reserve' : 'np_sub.none';
    else if (lone) npState = 'np_sub.lone';
    else if (anyDown) npState = 'np_sub.took_over';
    swapText(npSub, tr(npState), failover);
    let gameState = 'game_sub.ok';
    if (!any) gameState = anyDown ? 'game_sub.kicked' : 'game_sub.waiting';
    else if (lone) gameState = 'game_sub.lone';
    swapText(gameSub, tr(gameState));
    // result row: the live figure is the combined capacity; the headline is the match's state
    sumEl.textContent = any ? fAll.n : '0';
    sumU.textContent = any ? fAll.u : tr('unit_mb');
    let headline = 'sum_b.ok';
    if (bad) headline = 'sum_b.out';
    else if (!any) headline = 'sum_b.none';
    else if (lone) headline = kicked ? 'sum_b.kicked' : 'sum_b.lone';
    sumB.textContent = tr(headline);
    sumRow.classList.toggle('is-bad', bad);
    sumRow.classList.toggle('is-weak', lone);
    let subline = resultNote;
    if (!subline) {
      const first = act[0];
      const second = act[1];
      if (!any || !first) subline = escapeHtml(tr('sum_sub.none'));
      else if (act.length === 1) subline = altKey(kicked ? 'sum_sub.lone_kicked' : 'sum_sub.lone', { name: nb(first.name) });
      else if (act.length === 2 && second) subline = altKey('sum_sub.pair', { a: nb(first.name), b: nb(second.name) });
      else subline = altKey('sum_sub.many', { count: act.length });
    }
    sumSub.innerHTML = subline;
    // "Simular falha" works with a single connection too: it then plays the honest outcome (out of the match)
    const canDrop = act.length >= 1 && !dropping && !(reduced.matches && anyDown && !any);
    dropBtn.setAttribute('aria-disabled', canDrop ? 'false' : 'true');
    let title = act.length < 2 ? 'drop_title_lone' : 'drop_title';
    if (dropping) title = 'drop_title_wait';
    else if (reduced.matches) title = anyDown ? 'drop_title_back' : 'drop_title';
    dropBtn.title = tr(title);
    // the "clique/toque para ativar" cue stays pinned while fewer than two connections are active
    hintText.classList.toggle('is-done', hintGone && act.length >= 2);
    if (running) setRates();
  }

  /* ---------- actions ---------- */
  function turnOn(id: string, burst: boolean) {
    const connection = byId(id);
    if (!connection) return;
    connection.on = true;
    connection.down = false;
    resultNote = null;
    syncDerived();
    if (burst) pulse(connection.el, 'is-burst');
  }
  function turnOff(id: string) {
    const connection = byId(id);
    if (!connection) return;
    connection.on = false;
    connection.down = false;
    resultNote = null;
    connection.el.classList.remove('is-burst');
    syncDerived();
  }
  function dropConn(id: string): Connection | null {
    const connection = byId(id);
    if (!connection || !connection.on || connection.down) return null;
    connection.down = true;
    const rest = active();
    if (!rest.length) kicked = true;
    const name = nb(connection.name);
    const first = rest[0];
    const second = rest[1];
    if (!rest.length || !first) resultNote = altKey('note.drop_lone', { name });
    else if (rest.length === 1) resultNote = altKey('note.drop_one', { name, other: nb(first.name) });
    else if (rest.length > 2 || !second) resultNote = tr('note.drop_many', { name, count: rest.length });
    else resultNote = altKey('note.drop_two', { name, a: nb(first.name), b: nb(second.name) });
    syncDerived();
    pulse(connection.el, 'is-shake');
    if (!active().length) {
      // the honest single-internet outcome: no reserve, no failover
      sayKey('say.drop_lone', { name }, true, true);
      return connection;
    }
    burstNode();
    sayKey('say.drop_covered', { name }, true, true);
    return connection;
  }
  /* the spoken version of a drop / recovery; only user-triggered beats are announced */
  function dropSr(connection: Connection): string {
    const others = active();
    if (!others.length) return tr('live.drop_lone', { name: connection.sr });
    const via = others.length > 2 ? tr('live.via_many', { count: others.length }) : joinList(others.map((other) => tr('live.via_item', { name: other.sr })));
    return tr('live.drop_covered', { name: connection.sr, via });
  }
  function recoverSr(connection: Connection): string {
    const act = active();
    if (act.length >= 2) return tr('live.back_many', { name: connection.sr, combo: combo(act.length) });
    return tr(kicked ? 'live.back_kicked' : 'live.back_lone', { name: connection.sr });
  }
  function recoverConn(id: string) {
    const connection = byId(id);
    if (!connection || !connection.down) return;
    const wasLone = !active().length;
    connection.down = false;
    connection.on = true;
    const count = active().length;
    const name = nb(connection.name);
    resultNote = wasLone ? altKey('note.back_lone', { name }) : altKey('note.back', { name, count });
    syncDerived();
    pulse(connection.el, 'is-burst');
    sayKey(wasLone ? 'say.back_lone' : 'say.back', { name }, true);
  }
  function setState(map: Record<string, boolean>) {
    CONNS.forEach((connection) => {
      connection.on = !!map[connection.id];
      connection.down = false;
      connection.el.classList.remove('is-burst');
    });
    resultNote = null;
    kicked = false;
    syncDerived();
  }

  /* ---------- auto-demo (looping script) ---------- */
  let lastDrop: string | null = null;
  let touched = false;
  const nextReserve = () => CONNS.find((connection) => !connection.on && !connection.down) ?? null;
  function pickDrop(): Connection | null {
    const act = active();
    if (act.length < 2) return null;
    const sorted = act.slice().sort((first, second) => second.mbps - first.mbps);
    const top = sorted[0] ?? null;
    return top && top.id === lastDrop && sorted.length > 1 ? (sorted[1] ?? top) : top;
  }
  function stepReset() {
    const extras = CONNS.filter((connection) => (connection.on || connection.down) && connection.id !== 'fibra' && connection.id !== 'wifi');
    setState({ fibra: true, wifi: true });
    if (extras.length) sayKey('say.reset', { count: extras.length, list: joinList(extras.map((connection) => nb(connection.name))) }, true);
    else sayKey('say.reset_plain', {}, true);
  }
  function stepAdd(second: boolean) {
    const connection = nextReserve();
    if (!connection) {
      sayKey('say.all_on', {}, true);
      return;
    }
    turnOn(connection.id, true);
    sayKey(second ? 'say.added_more' : 'say.added', { name: nb(connection.name) }, true);
  }
  function stepDrop() {
    const target = pickDrop();
    if (!target) {
      if (!touched) stepAdd(false);
      return;
    }
    lastDrop = target.id;
    dropConn(target.id);
  }
  function stepRecover() {
    const fallen = CONNS.find((connection) => connection.down);
    if (fallen) recoverConn(fallen.id);
  }
  // scripted story (untouched visitor): pair → +5G → falha → volta → +Cabo → falha → volta → narrated restart
  const STEPS = [
    { wait: 3600, run: stepReset },
    { wait: 3600, run: () => stepAdd(false) },
    { wait: 3900, run: stepDrop },
    { wait: 4200, run: stepRecover },
    { wait: 3600, run: () => stepAdd(true) },
    { wait: 3900, run: stepDrop },
    { wait: 4600, run: stepRecover },
  ];
  // once touched, the add/remove beats stop for good; after a quiet spell the failover beat plays on the visitor's own set
  const STEPS_USER = [
    {
      wait: 4600,
      run: () => {
        if (active().length >= 2) stepDrop();
      },
    },
    { wait: 9500, run: stepRecover },
  ];
  const USER_IDLE = 8000;
  let stepIdx = 1;
  let lastMove = 0;
  function engaged(): boolean {
    let keyboard = false;
    try {
      keyboard = !!list.querySelector(':focus-visible');
    } catch {
      keyboard = list.contains(document.activeElement);
    }
    return keyboard || (!!hoverId && Date.now() - lastMove < 6000);
  }
  const canRun = () => built && visible && !document.hidden && !reduced.matches && !paused && !dropping && !engaged();
  const dormant = () => !visible || document.hidden || paused || reduced.matches;
  function schedule(ms: number) {
    window.clearTimeout(stepTimer);
    stepTimer = window.setTimeout(tickStep, ms);
  }
  function tickStep() {
    stepTimer = 0;
    if (!canRun()) {
      if (!dormant()) schedule(700);
      return;
    }
    const steps = touched ? STEPS_USER : STEPS;
    if (touched && stepIdx >= steps.length) stepIdx = 0;
    if (touched && stepIdx === 0 && CONNS.some((connection) => connection.down)) stepIdx = 1;
    const step = steps[stepIdx];
    if (!step) return;
    step.run();
    stepIdx = (stepIdx + 1) % steps.length;
    schedule(step.wait);
  }
  function takeOver() {
    touched = true;
    stepIdx = 0;
    window.clearTimeout(stepTimer);
    stepTimer = 0;
    if (!dormant()) schedule(USER_IDLE);
  }

  /* ---------- user interaction ---------- */
  function dismissHint() {
    if (hintGone) return;
    hintGone = true;
    if (active().length >= 2) {
      hintText.classList.add('is-done');
      flip(hintText);
    }
  }
  const loneMsg = (connection: Connection) => rich(tr(kicked ? 'say.lone_kicked' : 'say.lone', { name: nb(connection.name) }));

  function onListClick(event: MouseEvent) {
    const button = (event.target as Element | null)?.closest?.('[data-conn]');
    if (!button) return;
    const connection = byId(button.getAttribute('data-conn'));
    if (!connection) return;
    takeOver();
    dismissHint();
    if (connection.down) {
      recoverConn(connection.id);
      live(recoverSr(connection));
      if (reduced.matches && !CONNS.some((other) => other.down)) dropLbl.textContent = tr('drop');
    } else if (connection.on) {
      if (active().length === 1) {
        // the last connection stays on: the showpiece never becomes a picture of a dead link
        pulse(connection.el, 'is-nudge');
        sayKey('say.keep_one', {}, true);
        live(tr('live.keep_one'));
        return;
      }
      turnOff(connection.id);
      const act = active();
      const only = act[0];
      if (act.length === 1 && only) say(loneMsg(only), true);
      else sayKey('say.to_reserve', { name: nb(connection.name) }, true);
      live(act.length === 1 && only ? tr('live.to_reserve_lone', { name: connection.sr, other: only.sr }) : tr('live.to_reserve', { name: connection.sr, combo: combo(act.length) }));
    } else {
      turnOn(connection.id, true);
      const now = active();
      if (now.length === 1) say(loneMsg(connection), true);
      else sayKey('say.joined', { name: nb(connection.name) }, true);
      live(now.length === 1 ? tr('live.on_lone', { name: connection.sr }) : tr('live.on', { name: connection.sr, combo: combo(now.length) }));
    }
    if (reduced.matches || !supportsMotion) drawStatic();
  }
  function onPointerOver(event: PointerEvent) {
    const button = (event.target as Element | null)?.closest?.('[data-conn]');
    if (!button || event.pointerType === 'touch') return;
    hoverId = button.getAttribute('data-conn');
    lastMove = Date.now();
  }
  function onPointerMove(event: PointerEvent) {
    if (event.pointerType !== 'touch') lastMove = Date.now();
  }
  function onPointerOut(event: PointerEvent) {
    const button = (event.target as Element | null)?.closest?.('[data-conn]');
    if (!button || event.pointerType === 'touch') return;
    if (event.relatedTarget instanceof Node && button.contains(event.relatedTarget)) return;
    hoverId = null;
  }
  const strongest = (connections: Connection[]) => connections.slice().sort((first, second) => second.mbps - first.mbps)[0] ?? null;
  function onDropClick() {
    if (dropBtn.getAttribute('aria-disabled') === 'true') {
      // a dimmed control still answers a tap: the reason lands in the visible status line
      if (!dropping) sayKey('say.need_one', {}, true);
      live(dropBtn.title);
      return;
    }
    const act = active();
    if (reduced.matches) {
      // no motion: the button swaps the two stills, the failover frame and the recovered frame
      takeOver();
      dismissHint();
      const fallen = CONNS.find((connection) => connection.down);
      const target = strongest(act);
      if (fallen) {
        recoverConn(fallen.id);
        dropLbl.textContent = tr('drop');
        live(recoverSr(fallen));
      } else if (target) {
        dropConn(target.id);
        dropLbl.textContent = tr('drop_back');
        live(dropSr(target));
      }
      drawStatic();
      return;
    }
    const target = strongest(act);
    if (!target) return;
    takeOver();
    dismissHint();
    dropping = true;
    dropBtn.classList.add('is-busy');
    dropBtn.setAttribute('aria-busy', 'true');
    dropLbl.textContent = tr('drop_busy');
    dropConn(target.id);
    bridge.classList.add('is-hot');
    live(dropSr(target));
    if (!supportsMotion) drawStatic();
    window.clearTimeout(dropTimer);
    dropTimer = window.setTimeout(() => {
      dropping = false;
      recoverConn(target.id);
      live(recoverSr(target));
      dropBtn.classList.remove('is-busy');
      dropBtn.setAttribute('aria-busy', 'false');
      dropLbl.textContent = tr('drop');
      syncDerived();
      if (!supportsMotion) drawStatic();
      takeOver();
    }, 3400);
  }
  const capitalize = (html: string) => html.replace(/^([^<\p{L}]*)(\p{L})/u, (_match, lead: string, letter: string) => lead + letter.toLocaleUpperCase(lang));
  function onPauseClick() {
    paused = !paused;
    root.classList.toggle('is-paused', paused);
    pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    pauseBtn.setAttribute('aria-label', tr(paused ? 'resume' : 'pause'));
    const act = active();
    const anyDown = CONNS.some((connection) => connection.down);
    const only = act[0];
    let state: string;
    if (anyDown) {
      if (!act.length || !only) state = tr('state.down_none');
      else if (act.length === 1) state = tr('state.down_one', { name: nb(only.name) });
      else state = tr('state.down_many', { count: act.length });
    } else if (act.length >= 2) state = tr('state.many', { combo: combo(act.length) });
    else if (only) state = tr('state.one', { name: nb(only.name) });
    else state = tr('state.none');
    if (paused) {
      stop();
      window.clearTimeout(stepTimer);
      stepTimer = 0;
      sayKey('say.paused', { state }, false);
    } else {
      syncRun();
      let key = 'say.resumed_none';
      if (act.length >= 2) key = 'say.resumed_many';
      else if (act.length === 1) key = 'say.resumed_one';
      sayKey(key, { state: capitalize(state) }, true);
    }
    live(tr(paused ? 'live.paused' : 'live.resumed'));
  }
  function onKeyDown(event: KeyboardEvent) {
    const focused = document.activeElement;
    if (event.key === 'Escape' && focused instanceof HTMLElement && list.contains(focused)) focused.blur();
  }
  list.addEventListener('click', onListClick);
  list.addEventListener('pointerover', onPointerOver);
  list.addEventListener('pointermove', onPointerMove);
  list.addEventListener('pointerout', onPointerOut);
  dropBtn.addEventListener('click', onDropClick);
  pauseBtn.addEventListener('click', onPauseClick);
  root.addEventListener('keydown', onKeyDown);
  disposers.push(() => {
    list.removeEventListener('click', onListClick);
    list.removeEventListener('pointerover', onPointerOver);
    list.removeEventListener('pointermove', onPointerMove);
    list.removeEventListener('pointerout', onPointerOut);
    dropBtn.removeEventListener('click', onDropClick);
    pauseBtn.removeEventListener('click', onPauseClick);
    root.removeEventListener('keydown', onKeyDown);
  });

  /* ---------- geometry ---------- */
  const tileMask: { el: SVGMaskElement | null; all: SVGRectElement | null; rects: SVGRectElement[] } = { el: null, all: null, rects: [] };
  const nodeShift = new WeakMap<HTMLElement, number>();
  function setTileMask(width: number, height: number, boxes: Rect[]) {
    const mask = tileMask;
    if (!mask.el || !mask.all) return;
    mask.el.setAttribute('x', '0');
    mask.el.setAttribute('y', '0');
    mask.el.setAttribute('width', String(round1(width)));
    mask.el.setAttribute('height', String(round1(height)));
    mask.all.setAttribute('width', String(round1(width)));
    mask.all.setAttribute('height', String(round1(height)));
    mask.rects.forEach((rect, index) => {
      const tile = boxes[index];
      if (tile) {
        rect.setAttribute('x', String(round1(tile.l)));
        rect.setAttribute('y', String(round1(tile.t)));
        rect.setAttribute('width', String(round1(tile.w)));
        rect.setAttribute('height', String(round1(tile.h)));
      } else {
        rect.setAttribute('width', '0');
        rect.setAttribute('height', '0');
      }
    });
  }
  function makeSet(cls: string): HTMLDivElement {
    const set = document.createElement('div');
    set.className = `pk-set${cls ? ` ${cls}` : ''}`;
    pkLayer.appendChild(set);
    return set;
  }
  function makePackets(set: HTMLDivElement, count: number): Packet[] {
    return Array.from({ length: count }, (_unused, index) => {
      const element = document.createElement('div');
      element.className = 'pk';
      set.appendChild(element);
      return { el: element, phase: (index + 0.15) / count, anim: null };
    });
  }
  const segment = (parks: number[]): Segment => ({ d: '', parks, packets: [], frames: null, parkIdx: null });
  function mkPath(cls: string, parent: Element): SVGPathElement {
    const path = document.createElementNS(NS, 'path');
    if (cls) path.setAttribute('class', cls);
    parent.appendChild(path);
    return path;
  }
  function svgEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string> = {}): SVGElementTagNameMap[K] {
    const element = document.createElementNS(NS, tag);
    Object.entries(attrs).forEach(([name, value]) => element.setAttribute(name, value));
    return element;
  }
  function buildStage() {
    if (built || destroyed) return;
    built = true;
    // every wire layer is drawn through one mask that cuts out the node tiles (and, on the phone, the six station tiles)
    const defs = svgEl('defs');
    tileMask.el = svgEl('mask', { id: 'mi-tm', class: 'w-tilemask', maskUnits: 'userSpaceOnUse' });
    tileMask.all = svgEl('rect', { class: 'w-maskall' });
    tileMask.el.appendChild(tileMask.all);
    for (let index = 0; index < 9; index += 1) {
      const rect = svgEl('rect', { width: '0', height: '0' });
      tileMask.el.appendChild(rect);
      tileMask.rects.push(rect);
    }
    defs.appendChild(tileMask.el);
    svg.appendChild(defs);
    const wires = svgEl('g', { mask: 'url(#mi-tm)' });
    svg.appendChild(wires);
    const portLayer = svgEl('g');
    const trunkGroup = svgEl('g', { class: 'w-trunkg' });
    trunk.base = mkPath('w-base', trunkGroup);
    trunk.glow = mkPath('w-glow', trunkGroup);
    const trunkHot = svgEl('g', { class: 'w-hotwrap' });
    trunk.hot = mkPath('w-hot', trunkHot);
    trunkGroup.appendChild(trunkHot);
    wires.appendChild(trunkGroup);
    trunk.g = trunkGroup;
    CONNS.forEach((connection) => {
      const group = svgEl('g', { 'data-wire': connection.id });
      const downWrap = svgEl('g', { class: 'w-downwrap' });
      connection.baseIn = mkPath('w-base w-in', downWrap);
      connection.baseOut = mkPath('w-base w-out', downWrap);
      connection.baseOut.setAttribute('pathLength', '100');
      group.appendChild(downWrap);
      connection.cut = svgEl('g', { class: 'w-cut' });
      connection.cut.appendChild(svgEl('circle', { r: '4.5' }));
      connection.cut.appendChild(svgEl('path', { d: 'M-1.9,-1.9 L1.9,1.9 M1.9,-1.9 L-1.9,1.9' }));
      downWrap.appendChild(connection.cut);
      connection.glowIn = mkPath('w-glow', group);
      connection.glowOut = mkPath('w-glow', group);
      const hotWrap = svgEl('g', { class: 'w-hotwrap' });
      connection.hotIn = mkPath('w-hot', hotWrap);
      connection.hotOut = mkPath('w-hot', hotWrap);
      group.appendChild(hotWrap);
      wires.appendChild(group);
      connection.g = group;
      // the card's two jack dots live outside the mask, in a twin group that mirrors the wire group's state
      connection.pg = svgEl('g');
      connection.portIn = svgEl('circle', { r: '2.75', class: 'w-rport w-rp-in' });
      connection.portOut = svgEl('circle', { r: '2.75', class: 'w-rport w-rp-out' });
      connection.pg.appendChild(connection.portIn);
      connection.pg.appendChild(connection.portOut);
      portLayer.appendChild(connection.pg);
      connection.set = makeSet('');
      connection.segIn = segment([0.38, 0.8]);
      connection.segIn.packets = makePackets(connection.set, 2);
      connection.segOut = segment([0.3, 0.6, 0.9]);
      connection.segOut.packets = makePackets(connection.set, 3);
    });
    trunk.set = makeSet('is-trunk');
    trunk.packets = makePackets(trunk.set, TRUNK_PHASES_BUS.length);
    fan.g = svgEl('g', { class: 'w-fan' });
    mkPath('', fan.g);
    wires.appendChild(fan.g);
    fan.pg = svgEl('g', { class: 'w-fan' });
    fan.pg.appendChild(svgEl('circle', { r: '3.25', class: 'w-fan-dot' }));
    fan.pg.appendChild(svgEl('circle', { r: '2.75', class: 'w-fan-cap' }));
    portLayer.appendChild(fan.pg);
    ['pcOut', 'npIn', 'npOut', 'gameIn'].forEach((portKey) => {
      const port = svgEl('circle', { r: '4', class: 'w-port' });
      portLayer.appendChild(port);
      ports[portKey] = port;
    });
    svg.appendChild(portLayer);
    syncDerived();
    layout(true);
  }
  function box(bounds: DOMRect, stageBounds: DOMRect): Rect {
    return { l: bounds.left - stageBounds.left, t: bounds.top - stageBounds.top, r: bounds.right - stageBounds.left, b: bounds.bottom - stageBounds.top, w: bounds.width, h: bounds.height, cx: bounds.left - stageBounds.left + bounds.width / 2, cy: bounds.top - stageBounds.top + bounds.height / 2 };
  }
  const rel = (element: Element, stageBounds: DOMRect) => box(element.getBoundingClientRect(), stageBounds);
  const BEND_A = 0.15;
  const BEND_B = 0.27;
  const PORT_D = 5.25;
  const JACK_D = 4;
  function cubR(x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    const c1 = Math.min(Math.max(dx * BEND_A, 10), dx * 0.45);
    return `C${round1(x1 + c1)},${round1(y1)} ${round1(x1 + Math.max(dx * BEND_B, c1 + 6))},${round1(y2)} ${round1(x2)},${round1(y2)}`;
  }
  function cubL(x1: number, y1: number, x2: number, y2: number) {
    const dx = x2 - x1;
    return `C${round1(x2 - dx * BEND_B)},${round1(y1)} ${round1(x2 - dx * BEND_A)},${round1(y2)} ${round1(x2)},${round1(y2)}`;
  }
  let lastKey = '';
  const setD = (path: SVGPathElement | undefined, d: string) => path?.setAttribute('d', d);
  function cutDash(fraction: number) {
    const out: number[] = [];
    let position = 0;
    const end = fraction * 100;
    while (position + 3 <= end) {
      out.push(3, 3.5);
      position += 6.5;
    }
    out.push(0, 100);
    return out.join(' ');
  }
  /* path geometry in plain JS (absolute M / L / C only): no getTotalLength, no forced reflow */
  function samplePath(d: string) {
    const tokens = d.match(/[MLC]|-?\d*\.?\d+/g) || [];
    let index = 0;
    const points: Array<{ x: number; y: number; s: number }> = [];
    let total = 0;
    let current: { x: number; y: number } | null = null;
    let command = '';
    const num = () => {
      const value = parseFloat(tokens[index] ?? '0');
      index += 1;
      return value;
    };
    const push = (x: number, y: number, jump: boolean) => {
      if (current && !jump) total += Math.hypot(x - current.x, y - current.y);
      current = { x, y };
      points.push({ x, y, s: total });
    };
    while (index < tokens.length) {
      const token = tokens[index];
      if (token === 'M' || token === 'L' || token === 'C') {
        command = token;
        index += 1;
      } else if (command === 'M') {
        push(num(), num(), true);
        command = 'L';
      } else if (command === 'L') push(num(), num(), false);
      else if (command === 'C' && current) {
        const { x: x0, y: y0 } = current as { x: number; y: number };
        const x1 = num();
        const y1 = num();
        const x2 = num();
        const y2 = num();
        const x3 = num();
        const y3 = num();
        for (let step = 1; step <= 24; step += 1) {
          const u = step / 24;
          const v = 1 - u;
          push(v * v * v * x0 + 3 * v * v * u * x1 + 3 * v * u * u * x2 + u * u * u * x3, v * v * v * y0 + 3 * v * v * u * y1 + 3 * v * u * u * y2 + u * u * u * y3, false);
        }
      } else index += 1;
    }
    return { pts: points, len: total };
  }
  function pointAt(poly: { pts: Array<{ x: number; y: number; s: number }>; len: number }, distance: number) {
    const { pts } = poly;
    const first = pts[0];
    const last = pts[pts.length - 1];
    if (!first || !last) return { x: 0, y: 0 };
    if (distance <= 0) return first;
    if (distance >= poly.len) return last;
    let lo = 0;
    let hi = pts.length - 1;
    while (hi - lo > 1) {
      const mid = Math.floor((lo + hi) / 2);
      if ((pts[mid]?.s ?? 0) <= distance) lo = mid;
      else hi = mid;
    }
    const from = pts[lo] ?? first;
    const end = pts[hi] ?? last;
    const span = end.s - from.s;
    const ratio = span > 0 ? (distance - from.s) / span : 0;
    return { x: from.x + (end.x - from.x) * ratio, y: from.y + (end.y - from.y) * ratio };
  }
  function placeCut(connection: Connection) {
    if (connection.cutXY) connection.cut?.setAttribute('transform', connection.cutXY);
    connection.baseOut?.style.setProperty('--mi-cut-dash', '25 20 25 100');
    connection.baseOut?.style.setProperty('--mi-dash-off', '-25');
  }
  function syncStub(connection: Connection) {
    if (busMode) {
      placeStation(connection);
      return;
    }
    if (!connection.stubD) return;
    connection.baseOut?.setAttribute('d', connection.down ? connection.stubD : (connection.segOut?.d ?? ''));
  }
  /* phone station: every card hangs off the unbroken spine by one short horizontal branch; fallen, the branch is severed */
  function placeStation(connection: Connection) {
    const geo = connection.busR;
    if (!geo) return;
    const { x } = geo;
    const { cy } = geo.card;
    const jx = geo.card.l - JACK_D;
    const { baseIn, baseOut, cut, portIn, portOut } = connection;
    if (!baseIn || !baseOut || !cut || !portIn || !portOut) return;
    if (connection.down) {
      baseIn.setAttribute('d', '');
      baseIn.style.removeProperty('stroke-dasharray');
      setD(connection.glowIn, '');
      setD(connection.hotIn, '');
      baseOut.setAttribute('d', `M${round1(jx)},${round1(cy)} L${round1(jx - 9)},${round1(cy)}`);
      baseOut.style.setProperty('--mi-cut-dash', '25 20 25 100');
      baseOut.style.setProperty('--mi-dash-off', '-25');
      cut.setAttribute('transform', `translate(${round1(jx - 13)},${round1(cy)})`);
      cut.removeAttribute('display');
      portIn.setAttribute('cx', String(round1(jx)));
      portIn.setAttribute('cy', String(round1(cy)));
      portOut.setAttribute('display', 'none');
      return;
    }
    const branch = `M${round1(x)},${round1(cy)} L${round1(jx)},${round1(cy)}`;
    baseIn.setAttribute('d', branch);
    baseIn.style.removeProperty('stroke-dasharray');
    setD(connection.glowIn, branch);
    setD(connection.hotIn, branch);
    baseOut.setAttribute('d', '');
    baseOut.style.setProperty('--mi-cut-dash', '30 35');
    baseOut.style.setProperty('--mi-dash-off', '0');
    cut.setAttribute('display', 'none');
    portIn.setAttribute('cx', String(round1(x)));
    portIn.setAttribute('cy', String(round1(cy)));
    portOut.removeAttribute('display');
    portOut.setAttribute('cx', String(round1(jx)));
    portOut.setAttribute('cy', String(round1(cy)));
  }
  /* phone bus: ONE unbroken vertical line in the gutter: Seu PC → NoPing → servidor */
  function busTrunk(geo: { x: number; pcB: number; npT: number; npB: number; gmT: number }) {
    const { x } = geo;
    return { d: `M${round1(x)},${round1(geo.pcB + 1)} L${round1(x)},${round1(geo.npT - 1)} M${round1(x)},${round1(geo.npB + 1)} L${round1(x)},${round1(geo.gmT - 1)}`, pk: `M${round1(x)},${round1(geo.pcB + 1)} L${round1(x)},${round1(geo.gmT - 1)}` };
  }
  function updateBusTrunk() {
    if (!busGeo) return;
    const key = CONNS.map((connection) => (connection.down ? 1 : 0)).join('');
    if (key === busKey) return;
    busKey = key;
    const result = busTrunk(busGeo);
    setD(trunk.base ?? undefined, result.d);
    setD(trunk.glow ?? undefined, result.d);
    setD(trunk.hot ?? undefined, result.d);
    trunk.d = result.pk;
    if (trunk.frames) setPath(trunk);
  }
  const shiftBox = (rect: Rect, dy: number): Rect => ({ l: rect.l, t: rect.t + dy, r: rect.r, b: rect.b + dy, w: rect.w, h: rect.h, cx: rect.cx, cy: rect.cy + dy });
  let motionArmed = false;
  function layout(force: boolean) {
    if (!built || destroyed) return;
    const stageBounds = svg.getBoundingClientRect();
    if (!stageBounds.width || !stageBounds.height) return;
    const mobile = isMobile();
    // every read first (one layout), then every write
    const rects = CONNS.map((connection) => rel(connection.el, stageBounds));
    const tiles = mobile ? CONNS.map((connection) => rel(connection.ccEl, stageBounds)) : [];
    const nodes = [pcNode, npNode, gameNode];
    const nodeRects = nodes.map((node) => rel(node, stageBounds));
    const iconRects = [pcIcon, npIcon, gameIcon].map((icon) => rel(icon, stageBounds));
    const firstRect = rects[0];
    const lastRect = rects[rects.length - 1];
    if (!firstRect || !lastRect) return;
    const center = (firstRect.t + lastRect.b) / 2;
    // all three node groups are centred on the connection-card stack, so the three tiles sit on ONE horizontal axis
    nodes.forEach((node, index) => {
      const current = nodeShift.get(node) || 0;
      const nodeRect = nodeRects[index];
      const iconRect = iconRects[index];
      if (!nodeRect || !iconRect) return;
      const dy = mobile ? 0 : center - ((nodeRect.t + nodeRect.b) / 2 - current);
      if (Math.abs(dy - current) > 0.5) {
        node.style.setProperty('--mi-dy', `${round1(dy)}px`);
        nodeShift.set(node, dy);
        iconRects[index] = shiftBox(iconRect, dy - current);
      }
    });
    const [pcR, npR, gmR] = iconRects;
    if (!pcR || !npR || !gmR) return;
    const key = [Math.round(stageBounds.width), Math.round(stageBounds.height), mobile ? 1 : 0, Math.round(firstRect.t), Math.round(lastRect.b), Math.round(pcR.t), Math.round(pcR.l), Math.round(npR.t), Math.round(npR.l), Math.round(gmR.t), Math.round(gmR.l), Math.round(firstRect.l)].join('|');
    if (!force && key === lastKey) return;
    lastKey = key;
    if (!pcR.w || !npR.w || !gmR.w || rects.some((rect) => !rect.w || !rect.h)) return;
    svg.setAttribute('viewBox', `0 0 ${stageBounds.width} ${stageBounds.height}`);
    setTileMask(stageBounds.width, stageBounds.height, [pcR, npR, gmR].concat(tiles));
    const modeChanged = busMode !== mobile;
    busMode = mobile;
    stage.classList.toggle('is-bus', mobile);
    trunk.live = mobile ? TRUNK_PHASES_BUS.length : TRUNK_PHASES_DESK.length;
    trunk.packets.forEach((packet, index) => {
      packet.phase = (mobile ? TRUNK_PHASES_BUS : TRUNK_PHASES_DESK)[index] || 0;
      packet.el.hidden = index >= trunk.live;
      // a packet not shown in this mode has no animation at all
      if ((modeChanged || packet.el.hidden) && packet.anim) {
        packet.anim.cancel();
        packet.anim = null;
      }
    });
    // the reserve routes are told by the backup card itself: no connector crosses the band
    fan.g?.setAttribute('display', 'none');
    fan.pg?.setAttribute('display', 'none');
    let d: string;
    let dPk: string;
    const setPort = (portKey: string, cx: number, cy: number) => {
      ports[portKey]?.setAttribute('cx', String(round1(cx)));
      ports[portKey]?.setAttribute('cy', String(round1(cy)));
    };
    if (!mobile) {
      // every stroke docks INTO a port dot just outside the border it arrives at
      const from = { x: pcR.r + PORT_D, y: pcR.cy };
      const to = { x: npR.l - PORT_D, y: npR.cy };
      CONNS.forEach((connection, index) => {
        const rect = rects[index];
        if (!rect) return;
        const dIn = `M${round1(from.x)},${round1(from.y)} ${cubL(from.x, from.y, rect.l - JACK_D, rect.cy)}`;
        const dOut = `M${round1(rect.r + JACK_D)},${round1(rect.cy)} ${cubR(rect.r + JACK_D, rect.cy, to.x, to.y)}`;
        setD(connection.baseIn, dIn);
        setD(connection.glowIn, dIn);
        setD(connection.hotIn, dIn);
        setD(connection.glowOut, dOut);
        setD(connection.hotOut, dOut);
        connection.portIn?.setAttribute('cx', String(round1(rect.l - JACK_D)));
        connection.portIn?.setAttribute('cy', String(round1(rect.cy)));
        connection.portOut?.setAttribute('cx', String(round1(rect.r + JACK_D)));
        connection.portOut?.setAttribute('cy', String(round1(rect.cy)));
        // a fallen card's severed stub is a straight run on the card's own centreline
        connection.stubD = `M${round1(rect.r + JACK_D)},${round1(rect.cy)} L${round1(rect.r + JACK_D + 12)},${round1(rect.cy)}`;
        connection.cutXY = `translate(${round1(rect.r + JACK_D + 17.5)},${round1(rect.cy)})`;
        setD(connection.baseOut, connection.down ? connection.stubD : dOut);
        if (connection.segIn) connection.segIn.d = dIn;
        if (connection.segOut) connection.segOut.d = dOut;
        connection.baseIn?.style.removeProperty('stroke-dasharray');
        connection.cut?.removeAttribute('display');
        connection.portOut?.removeAttribute('display');
        connection.busR = null;
      });
      // NoPing → server: the same curve vocabulary as the fans (a gentle rise)
      const trunkStart = { x: npR.r + PORT_D, y: npR.cy };
      const end = { x: gmR.l - PORT_D, y: gmR.cy };
      const tdx = end.x - trunkStart.x;
      const rise = Math.min(12, tdx * 0.14);
      d = `M${round1(trunkStart.x)},${round1(trunkStart.y)} C${round1(trunkStart.x + tdx * 0.34)},${round1(trunkStart.y - rise)} ${round1(end.x - tdx * 0.34)},${round1(end.y - rise)} ${round1(end.x)},${round1(end.y)}`;
      dPk = d;
      setPort('pcOut', pcR.r + PORT_D, pcR.cy);
      setPort('npIn', npR.l - PORT_D, npR.cy);
      setPort('npOut', npR.r + PORT_D, npR.cy);
      setPort('gameIn', gmR.l - PORT_D, gmR.cy);
      trunk.hide = null;
      busGeo = null;
      busKey = '';
    } else {
      const x = pcR.cx;
      busGeo = { x, pcB: pcR.b, npT: npR.t, npB: npR.b, gmT: gmR.t };
      busKey = CONNS.map((connection) => (connection.down ? 1 : 0)).join('');
      const bus = busTrunk(busGeo);
      d = bus.d;
      dPk = bus.pk;
      trunk.hide = tiles.concat([npR]).map((tile) => ({ l: tile.l - 1, t: tile.t - 1, r: tile.r + 1, b: tile.b + 1 }));
      CONNS.forEach((connection, index) => {
        const tile = tiles[index];
        const card = rects[index];
        if (!tile || !card) return;
        connection.busR = { x, tile, card };
        setD(connection.glowIn, '');
        setD(connection.hotIn, '');
        setD(connection.glowOut, '');
        setD(connection.hotOut, '');
        if (connection.segIn) connection.segIn.d = '';
        if (connection.segOut) connection.segOut.d = '';
        placeStation(connection);
      });
      setPort('pcOut', x, pcR.b + PORT_D);
      setPort('npIn', x, npR.t - PORT_D);
      setPort('npOut', x, npR.b + PORT_D);
      setPort('gameIn', x, gmR.t - PORT_D);
    }
    setD(trunk.base ?? undefined, d);
    setD(trunk.glow ?? undefined, d);
    setD(trunk.hot ?? undefined, d);
    trunk.d = dPk;
    svg.style.setProperty('--mi-cut-dash', cutDash(0.42));
    if (!mobile) CONNS.forEach(placeCut);
    if (modeChanged) {
      // a mode switch retires the per-connection packet animations (the phone bus has none)
      CONNS.forEach((connection) => {
        if (!connection.segIn || !connection.segOut) return;
        setPackets(connection).forEach((packet) => {
          if (packet.anim) {
            packet.anim.cancel();
            packet.anim = null;
          }
          packet.el.style.removeProperty('--pk-t');
        });
        connection.segIn.frames = null;
        connection.segOut.frames = null;
      });
      syncDerived();
    }
    stage.classList.add('is-ready');
    if (motionArmed) scheduleMotion();
  }

  /* ---------- packets (WAAPI along the wires) ---------- */
  function allSegs(): Segment[] {
    const out: Segment[] = [];
    CONNS.forEach((connection) => {
      if (connection.segIn && connection.segOut) out.push(connection.segIn, connection.segOut);
    });
    out.push(trunk);
    return out;
  }
  const inRects = (point: { x: number; y: number }, rects: Array<{ l: number; t: number; r: number; b: number }>) => rects.some((rect) => point.x >= rect.l && point.x <= rect.r && point.y >= rect.t && point.y <= rect.b);
  function sampleFrames(d: string, parks: number[], hide: Array<{ l: number; t: number; r: number; b: number }> | null) {
    const poly = samplePath(d);
    const { len } = poly;
    if (!len) return null;
    const count = Math.max(24, Math.min(200, Math.round(len / 6)));
    const points = Array.from({ length: count + 1 }, (_unused, index) => pointAt(poly, (len * index) / count));
    const frames: Keyframe[] = [];
    const visibleAt: boolean[] = [];
    let previousAngle = 0;
    points.forEach((point, index) => {
      const before = points[Math.max(0, index - 1)] ?? point;
      const after = points[Math.min(count, index + 1)] ?? point;
      let angle = (Math.atan2(after.y - before.y, after.x - before.x) * 180) / Math.PI;
      if (index) {
        while (angle - previousAngle > 180) angle -= 360;
        while (angle - previousAngle < -180) angle += 360;
      }
      previousAngle = angle;
      const frame: Keyframe = { offset: index / count, transform: `translate(${round1(point.x)}px,${round1(point.y)}px) rotate(${round1(angle)}deg)` };
      const shown = !hide || !inRects(point, hide);
      if (hide) frame.opacity = shown ? 1 : 0;
      visibleAt.push(shown);
      frames.push(frame);
    });
    // a parked (static) packet always rests on a visible stretch
    const parkIdx = parks.map((park) => {
      let index = Math.max(0, Math.min(count, Math.round(park * count)));
      let step = 0;
      while (!visibleAt[index] && step < count) {
        step += 1;
        index = Math.min(count, index + 1);
      }
      return index;
    });
    return { frames, parkIdx, len };
  }
  function makeAnim(packet: Packet, frames: Keyframe[]) {
    packet.anim = packet.el.animate(frames, { duration: BASE_MS, iterations: Infinity, easing: 'linear', delay: -packet.phase * BASE_MS });
  }
  function setPath(owner: Segment) {
    if (!owner.d) return;
    const result = sampleFrames(owner.d, owner.parks, owner.hide || null);
    if (!result) return;
    owner.frames = result.frames;
    owner.parkIdx = result.parkIdx;
    owner.len = result.len;
    owner.packets.forEach((packet, index) => {
      if (packet.el.hidden) return;
      if (packet.anim) {
        try {
          (packet.anim.effect as KeyframeEffect).setKeyframes(result.frames);
        } catch {
          const time = packet.anim.currentTime;
          packet.anim.cancel();
          packet.anim = null;
          makeAnim(packet, result.frames);
          if (packet.anim && time != null) (packet.anim as Animation).currentTime = time;
        }
      } else if (!running) {
        const parked = result.frames[result.parkIdx[index % result.parkIdx.length] ?? 0];
        packet.el.style.setProperty('--pk-t', String(parked?.transform ?? ''));
      }
    });
  }
  let motionQueue: Segment[] = [];
  let motionRaf = 0;
  function scheduleMotion() {
    if (!built || destroyed) return;
    motionQueue = allSegs();
    cancelAnimationFrame(motionRaf);
    motionRaf = requestAnimationFrame(motionStep);
  }
  function motionStep() {
    const next = motionQueue.shift();
    if (next) setPath(next);
    if (motionQueue.length) {
      motionRaf = requestAnimationFrame(motionStep);
      return;
    }
    motionRaf = 0;
    stage.classList.add('is-motion');
    if (reduced.matches || !supportsMotion) drawStatic();
    else syncRun();
  }
  function armMotion() {
    if (motionArmed) return;
    buildStage();
    motionArmed = true;
    if (stage.classList.contains('is-ready')) scheduleMotion();
  }
  function ensureAnims(owner: Segment) {
    const { frames } = owner;
    if (!frames) return;
    owner.packets.forEach((packet) => {
      if (packet.anim || packet.el.hidden) return;
      packet.el.style.removeProperty('--pk-t');
      makeAnim(packet, frames);
    });
  }
  function setRates() {
    CONNS.forEach((connection) => {
      if (!connection.segIn || !connection.segOut) return;
      const rate = rateFor(connection);
      connection.segIn.packets.concat(connection.segOut.packets).forEach((packet) => {
        if (packet.anim) packet.anim.playbackRate = rate;
      });
    });
    // the trunk moves at a steady ~170 px/s whatever its length, clamped so it never races or crawls
    const trunkRate = trunk.len ? Math.max(0.5, Math.min(2.2, ((BASE_MS / 1000) * 170) / trunk.len)) : 1;
    trunk.packets.forEach((packet) => {
      if (packet.anim) packet.anim.playbackRate = trunkRate * boost;
    });
  }
  function eachAnim(callback: (animation: Animation) => void) {
    allSegs().forEach((owner) => owner.packets.forEach((packet) => packet.anim && callback(packet.anim)));
  }
  function start() {
    if (reduced.matches || paused || !supportsMotion || !built) return;
    running = true;
    root.classList.add('is-live');
    allSegs().forEach(ensureAnims);
    setRates();
    // only the sets in the combo run; hidden sets stay paused
    CONNS.forEach((connection) => {
      const on = connection.on && !connection.down;
      setPackets(connection).forEach((packet) => {
        if (!packet.anim) return;
        if (on) {
          if (packet.anim.playState !== 'running') packet.anim.play();
        } else packet.anim.pause();
      });
    });
    const anyOn = active().length > 0;
    trunk.packets.forEach((packet) => {
      if (!packet.anim) return;
      if (anyOn) {
        if (packet.anim.playState !== 'running') packet.anim.play();
      } else packet.anim.pause();
    });
    sparksRun(true);
  }
  function stop() {
    running = false;
    root.classList.remove('is-live');
    sparksRun(false);
    eachAnim((animation) => animation.pause());
  }
  function drawStatic() {
    running = false;
    root.classList.remove('is-live');
    sparksRun(false);
    allSegs().forEach((owner) => {
      const { frames, parkIdx } = owner;
      if (!frames || !parkIdx) return;
      owner.packets.forEach((packet, index) => {
        if (packet.anim) {
          packet.anim.cancel();
          packet.anim = null;
        }
        packet.el.style.setProperty('--pk-t', String(frames[parkIdx[index % parkIdx.length] ?? 0]?.transform ?? ''));
      });
    });
  }

  /* ---------- run / visibility ---------- */
  function syncRun() {
    if (destroyed) return;
    const on = visible && !document.hidden;
    root.classList.toggle('is-idle', !on);
    if (on) {
      armMotion();
      start();
      if (!stepTimer && !paused && !reduced.matches) schedule(touched ? USER_IDLE : 600);
    } else {
      stop();
      window.clearTimeout(stepTimer);
      stepTimer = 0;
    }
  }
  let buildTimer = 0;
  if (typeof window.IntersectionObserver === 'function') {
    const buildObserver = new IntersectionObserver(
      (entries, observer) => {
        if (entries[0]?.isIntersecting) {
          buildStage();
          observer.disconnect();
        }
      },
      { rootMargin: '320px 0px' }
    );
    buildObserver.observe(stage);
    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        visible = !!entries[0]?.isIntersecting;
        syncRun();
      },
      { threshold: 0.05 }
    );
    visibilityObserver.observe(stage);
    buildTimer = window.setTimeout(() => {
      if (built || document.hidden) return;
      const rect = stage.getBoundingClientRect();
      if (rect.bottom > -320 && rect.top < window.innerHeight + 320) buildStage();
    }, 1500);
    disposers.push(() => {
      buildObserver.disconnect();
      visibilityObserver.disconnect();
    });
  } else {
    visible = true;
    armMotion();
  }
  document.addEventListener('visibilitychange', syncRun);
  disposers.push(() => document.removeEventListener('visibilitychange', syncRun));
  let resizeRaf = 0;
  const scheduleLayout = () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => layout(false));
  };
  if (typeof window.ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(scheduleLayout);
    resizeObserver.observe(stage);
    disposers.push(() => resizeObserver.disconnect());
  } else {
    window.addEventListener('resize', scheduleLayout);
    disposers.push(() => window.removeEventListener('resize', scheduleLayout));
  }
  if (document.fonts?.ready)
    document.fonts.ready.then(() => {
      if (!destroyed) scheduleLayout();
    });
  const onReducedChange = () => {
    if (reduced.matches) {
      stop();
      drawStatic();
    } else syncRun();
  };
  reduced.addEventListener('change', onReducedChange);
  disposers.push(() => reduced.removeEventListener('change', onReducedChange));

  /* ---------- boot ---------- */
  // reduced motion rests on the confident hero (two connections summed, "Conexão estável", zero quedas)
  syncDerived();
  if (!reduced.matches) schedule(STEPS[0]?.wait ?? 3600);

  return {
    destroy() {
      destroyed = true;
      disposers.forEach((dispose) => dispose());
      [stepTimer, dropTimer, snapTimer, buildTimer, trunk.offT].forEach((timer) => window.clearTimeout(timer));
      CONNS.forEach((connection) => window.clearTimeout(connection.offT));
      window.clearInterval(sparkTimer);
      [pulseRaf, snapRaf, motionRaf, resizeRaf].forEach((frame) => cancelAnimationFrame(frame));
      eachAnim((animation) => animation.cancel());
      svg.replaceChildren();
      svg.removeAttribute('viewBox');
      svg.style.removeProperty('--mi-cut-dash');
      pkLayer.replaceChildren();
      [pcNode, npNode, gameNode].forEach((node) => node.style.removeProperty('--mi-dy'));
      root.classList.remove('is-js', 'is-live', 'is-idle', 'is-paused', 'is-offline', 'is-weak');
      pauseBtn.setAttribute('aria-pressed', 'false');
      pauseBtn.setAttribute('aria-label', tr('pause'));
      dropBtn.classList.remove('is-busy');
      dropBtn.setAttribute('aria-busy', 'false');
      dropLbl.textContent = tr('drop');
      bridge.classList.remove('is-hot');
      stage.classList.remove('is-ready', 'is-motion', 'is-bus', 'is-failover');
    },
  };
}
